import { Button } from '@lms/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@lms/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@lms/ui/dropdown-menu';
import { Input } from '@lms/ui/input';
import { useToast } from '@lms/ui/use-toast';
import { EllipsisVerticalIcon, PencilIcon, TrashIcon } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { useTranslations } from 'use-intl';
import { SubmitButton } from '@/components/submit-button';
import { removeCode, renameCode } from '@/features/codes/codes-api';

// Ports features/codes/components/code-list-overflow-menu.tsx, which wrapped the
// shared DeleteConfirmationMenu. That helper is not available in the SPA yet, so
// the confirmation dialog is inlined here. Revalidation happens via `onChanged`.
export default function OverflowMenu({
  id,
  name,
  onChanged,
}: {
  id: number;
  name: string | null;
  onChanged: () => void;
}) {
  const t = useTranslations('admin');
  const { toast } = useToast();
  const [warningOpen, setWarningOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);
  const editNameLabel = t('edit', { what: t('name').toLowerCase() });

  const openRename = () => {
    setDraftName(name ?? '');
    setRenameOpen(true);
  };

  const handleRename = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsRenaming(true);
    try {
      const result = await renameCode(id, draftName);
      if (result.ok) {
        setRenameOpen(false);
        onChanged();
      } else {
        toast({ variant: 'destructive', title: result.error });
      }
    } finally {
      setIsRenaming(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const result = await removeCode(id);
      if (result.ok) {
        setWarningOpen(false);
        onChanged();
      } else {
        toast({ variant: 'destructive', title: result.error });
      }
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Dialog open={warningOpen} onOpenChange={setWarningOpen}>
        <DialogTrigger />
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('are_you_sure')}</DialogTitle>
          </DialogHeader>
          <Button
            variant="destructive"
            className="w-full"
            onClick={handleDelete}
            disabled={isDeleting}
          >
            <TrashIcon className="mr-2 h-4 w-4" />
            {t('remove', { what: '' }).trim()}
          </Button>
        </DialogContent>
      </Dialog>
      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editNameLabel}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRename} className="flex flex-col gap-4">
            <Input
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              placeholder={t('name')}
              autoFocus
            />
            <SubmitButton label={t('save')} pending={isRenaming} />
          </form>
        </DialogContent>
      </Dialog>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger>
          <EllipsisVerticalIcon />
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem onClick={openRename}>
            <PencilIcon className="mr-2 inline-block h-4 w-4" />
            {editNameLabel}
          </DropdownMenuItem>
          <DropdownMenuItem
            className="text-destructive"
            onClick={() => setWarningOpen(true)}
          >
            <TrashIcon className="mr-2 inline-block h-4 w-4" />
            {t('remove', { what: '' }).trim()}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}
